import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { filterStorage } from '../../components/analytics/filters/utils/filterStorage';
import { clearCsrfToken, setCsrfToken } from '../../utils/csrf';

interface Session {
  approle?: string | null;
  expiresAt: string;
  iselevated: boolean;
  isglobal: boolean;
  registrantid: string;
  sid: string;
  sidlastts: string;
  sidlogonts: string;
  sidstatus: string;
  timeToExpiryMs: number;
  csrfToken?: string;
}

interface UseSessionReturn {
  isAuthenticated: boolean;
  loading: boolean;
  validateSession: () => Promise<void>;
  logout: () => void;
  session: Session | null;
}

const SESSION_REFETCH_INTERVAL = 5 * 60 * 1000;
const SESSION_STALE_TIME = 5 * 60 * 1000;
const SESSION_GC_TIME = 10 * 60 * 1000;

export const SESSION_QUERY_KEY = 'session';

export function useSession(): UseSessionReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = () => {
    try {
      filterStorage.clear();
    } catch {
      // Ignore storage clear failures during logout.
    }
    clearCsrfToken();
    queryClient.setQueryData([SESSION_QUERY_KEY], null);
    navigate({ to: '/login' });
  };

  const {
    isLoading: loading,
    refetch,
    data: session,
  } = useQuery({
    queryKey: [SESSION_QUERY_KEY],
    queryFn: async () => {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });

      if (!response.ok) {
        logout();
        return null;
      }

      const sessionData = (await response.json()) as Session;

      // Store CSRF token if present
      if (sessionData.csrfToken) {
        setCsrfToken(sessionData.csrfToken);
      }

      return sessionData;
    },
    refetchInterval: SESSION_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: false,
    staleTime: SESSION_STALE_TIME,
    gcTime: SESSION_GC_TIME,
  });

  const validateSession = async () => {
    await refetch();
  };

  return {
    isAuthenticated: !!session,
    loading,
    validateSession,
    logout,
    session: session ?? null,
  };
}
